import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faHashtag,
  faExclamationTriangle,
  faCalendarAlt,
  faPhoneAlt,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { formatDateTime } from './TicketCustomerSidebar/utils';

const TicketCombinedBanner = ({
  idTicketDestino,
  ticket = {},
  onNavigateToTarget,
  onSwapPrincipal,
  isSwapping = false
}) => {
  const context = useContext(AppContext);
  const isDark =
    context?.config?.isDark ??
    (typeof document !== 'undefined' &&
      (document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark')));

  if (!idTicketDestino) return null;

  const incidencia = ticket.vAsunto || ticket.subject || 'Sin incidencia asignada';
  const fechaRaw = ticket.dFechaCierre || ticket.dUltimaActividad || ticket.dCreatedDate;
  const fechaTexto = fechaRaw ? formatDateTime(fechaRaw) : 'Sin fecha';
  const telefono = ticket.phone || ticket.ticketPhone || ticket.vTelefono || 'Sin teléfono';

  const pillStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff',
    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    borderRadius: '6px',
    padding: '3px 9px',
    fontSize: '0.78rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap'
  };

  return (
    <div
      className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2"
      style={{
        backgroundColor: isDark ? 'rgba(99, 102, 241, 0.10)' : '#f5f7ff',
        borderColor: isDark ? 'rgba(99, 102, 241, 0.20)' : '#e0e7ff'
      }}
    >
      {/* Lado Izquierdo: Icono + Título + Pills */}
      <div className="d-flex align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 me-1">
          <FontAwesomeIcon
            icon={faExchangeAlt}
            style={{
              color: isDark ? '#818cf8' : '#4f46e5',
              fontSize: '0.85rem'
            }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: '0.82rem',
              color: isDark ? '#e0e7ff' : '#1e1b4b'
            }}
          >
            Ticket combinado:
          </span>
        </div>

        {/* Pill 1: # Ticket #ID */}
        <div style={pillStyle}>
          <FontAwesomeIcon
            icon={faHashtag}
            style={{ color: isDark ? '#818cf8' : '#4f46e5', fontSize: '0.72rem' }}
          />
          <span style={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}>
            Ticket #{idTicketDestino}
          </span>
        </div>

        {/* Pill 2: Incidencia */}
        <div style={pillStyle}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{ color: '#f59e0b', fontSize: '0.72rem' }}
          />
          <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Incidencia:</span>
          <span style={{ fontWeight: 500, color: isDark ? '#f1f5f9' : '#1e293b' }}>
            {incidencia}
          </span>
        </div>

        {/* Pill 3: Fecha */}
        <div style={pillStyle}>
          <FontAwesomeIcon
            icon={faCalendarAlt}
            style={{ color: isDark ? '#818cf8' : '#6366f1', fontSize: '0.72rem' }}
          />
          <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Fecha:</span>
          <span style={{ fontWeight: 500, color: isDark ? '#f1f5f9' : '#1e293b' }}>
            {fechaTexto}
          </span>
        </div>

        {/* Pill 4: Teléfono */}
        <div style={pillStyle}>
          <FontAwesomeIcon
            icon={faPhoneAlt}
            style={{ color: '#10b981', fontSize: '0.72rem' }}
          />
          <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Teléfono:</span>
          <span style={{ fontWeight: 500, color: isDark ? '#f1f5f9' : '#1e293b' }}>
            {telefono}
          </span>
        </div>
      </div>

      {/* Lado Derecho: Botones de Acción */}
      <div className="d-flex align-items-center gap-2 ms-auto">
        {onSwapPrincipal && (
          <button
            type="button"
            disabled={isSwapping}
            onClick={onSwapPrincipal}
            className="btn btn-sm d-flex align-items-center gap-1 shadow-none"
            style={{
              backgroundColor: isDark ? '#312e81' : '#e0e7ff',
              borderColor: isDark ? '#4338ca' : '#c7d2fe',
              color: isDark ? '#e0e7ff' : '#3730a3',
              fontSize: '0.78rem',
              fontWeight: 600,
              padding: '4px 14px',
              borderRadius: '6px'
            }}
            title="Convertir este ticket en el ticket principal"
          >
            <FontAwesomeIcon icon={faStar} style={{ fontSize: '0.75rem', color: '#f59e0b' }} />
            <span>{isSwapping ? 'Cambiando...' : 'Hacer Principal'}</span>
          </button>
        )}
        <a
          href={`/support-engine/ticket/${idTicketDestino}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (onNavigateToTarget) {
              e.preventDefault();
              onNavigateToTarget(idTicketDestino);
            }
          }}
          className="btn btn-sm d-flex align-items-center shadow-none text-decoration-none"
          style={{
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderColor: isDark ? '#6366f1' : '#c7d2fe',
            color: isDark ? '#a5b4fc' : '#4f46e5',
            fontSize: '0.78rem',
            fontWeight: 600,
            padding: '4px 14px',
            borderRadius: '6px'
          }}
          title={`Abrir Ticket Principal #${idTicketDestino} en una nueva pestaña`}
        >
          Ir al Ticket Principal #{idTicketDestino} →
        </a>
      </div>
    </div>
  );
};

TicketCombinedBanner.propTypes = {
  idTicketDestino: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ticket: PropTypes.object,
  onNavigateToTarget: PropTypes.func,
  onSwapPrincipal: PropTypes.func,
  isSwapping: PropTypes.bool
};

export default TicketCombinedBanner;
